import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  applyActionCode,
  checkActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import { auth } from "../../../config/firebaseConfig";
import { useDispatch } from "react-redux";
import { login } from "../../../redux/features/authSlice";
import dbService from "../../../firebase/dbService";
import Header from "../../landingPage/Header";
import Footer from "../../landingPage/Footer";
import {
  LuCheckCircle2,
  LuAlertTriangle,
  LuLoader2,
  LuArrowRight,
  LuLogIn,
  LuHome,
  LuLock,
  LuEye,
  LuEyeOff,
  LuKeyRound,
} from "react-icons/lu";
import { toast } from "react-toastify";

// Helper to obtain an Auth instance matching the exact apiKey in the action link
const getActionAuth = (apiKeyParam) => {
  if (apiKeyParam && apiKeyParam !== auth?.config?.apiKey) {
    try {
      const appName = `action-auth-${apiKeyParam}`;
      const dynamicApp = getApps().some((a) => a.name === appName)
        ? getApp(appName)
        : initializeApp({ apiKey: apiKeyParam }, appName);
      return getAuth(dynamicApp);
    } catch (e) {
      console.warn("Could not initialize dynamic auth with apiKey from URL:", e);
    }
  }
  return auth;
};

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");
  const apiKey = searchParams.get("apiKey");
  const continueUrl = searchParams.get("continueUrl");

  // Status: 'verifying' | 'success' | 'error' | 'reset_form' | 'reset_success'
  const [status, setStatus] = useState("verifying");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Password reset states
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent double execution in React StrictMode
  const actionAttemptedRef = useRef(false);

  useEffect(() => {
    if (mode === "resetPassword") {
      if (status === "reset_success") {
        document.title = "Password Reset Successful | Practido";
      } else if (status === "error") {
        document.title = "Password Reset Issue | Practido";
      } else {
        document.title = "Reset Password | Practido";
      }
    } else {
      if (status === "success") {
        document.title = "Email Verified | Practido";
      } else if (status === "error") {
        document.title = "Verification Issue | Practido";
      } else {
        document.title = "Verify Your Email | Practido";
      }
    }
  }, [mode, status]);

  useEffect(() => {
    if (actionAttemptedRef.current) return;
    actionAttemptedRef.current = true;

    const performAction = async () => {
      const actionAuth = getActionAuth(apiKey);

      // 1. Validate incoming code parameter
      if (!oobCode) {
        if (mode === "resetPassword") {
          setStatus("error");
          setErrorMessage(
            "Missing password reset code. Please make sure you clicked the full link from your email."
          );
          return;
        }

        if (auth.currentUser) {
          try {
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
              setIsLoggedIn(true);
              setVerifiedEmail(auth.currentUser.email || "");
              setStatus("success");
              return;
            }
          } catch (_) {}
        }
        setStatus("error");
        setErrorMessage(
          "Missing verification code. Please make sure you clicked the full link from your email."
        );
        return;
      }

      // 2. Handle Password Reset Mode
      if (mode === "resetPassword") {
        try {
          const emailFromCode = await verifyPasswordResetCode(actionAuth, oobCode);
          setVerifiedEmail(emailFromCode || "");
          setStatus("reset_form");
        } catch (err) {
          console.error("Password reset verification failed:", err);
          setStatus("error");
          switch (err.code) {
            case "auth/expired-action-code":
              setErrorMessage(
                "This password reset link has expired. Please request a new password reset link."
              );
              break;
            case "auth/invalid-action-code":
              setErrorMessage(
                "This password reset link is invalid or has already been used. Please request a new link."
              );
              break;
            case "auth/user-disabled":
              setErrorMessage(
                "This account has been disabled. Please contact Practido support."
              );
              break;
            default:
              setErrorMessage(
                err.message ||
                  "Failed to verify password reset link. Please try requesting a new link."
              );
          }
        }
        return;
      }

      // 3. Reject other unexpected modes
      if (mode && mode !== "verifyEmail") {
        setStatus("error");
        setErrorMessage(
          `Invalid action mode: ${mode}. Expected email verification or password reset.`
        );
        return;
      }

      // 4. Handle Email Verification Mode
      try {
        let emailFromCode = "";
        try {
          const actionCodeInfo = await checkActionCode(actionAuth, oobCode);
          emailFromCode = actionCodeInfo?.data?.email || "";
          if (emailFromCode) {
            setVerifiedEmail(emailFromCode);
          }
        } catch (checkErr) {
          console.warn("Could not checkActionCode before applying:", checkErr);
        }

        await applyActionCode(actionAuth, oobCode);

        if (auth.currentUser) {
          setIsLoggedIn(true);
          try {
            await auth.currentUser.reload();
            const user = auth.currentUser;

            const existingUserMeta = await dbService
              .getdocument("user", user.uid)
              .catch(() => null);
            if (!existingUserMeta) {
              const userMeta = {
                uid: user.uid,
                email: user.email || emailFromCode,
                name: user.displayName || "",
                accountType: "parent",
                createdAt: Timestamp.now(),
              };
              await dbService.createDocumentWithId("user", userMeta, user.uid);

              const roleDocData = {
                uid: user.uid,
                email: user.email || emailFromCode,
                name: user.displayName || "",
                accountType: "parent",
                createdAt: Timestamp.now(),
              };
              await dbService.createDocumentWithId("parents", roleDocData, user.uid);
              sessionStorage.setItem("open_verify_phone_modal_on_load", "true");
            }

            dispatch(login("")).unwrap().catch(() => null);
          } catch (syncErr) {
            console.error("Error syncing profile after email verification:", syncErr);
          }
        }

        setStatus("success");
      } catch (err) {
        console.error("Email verification failed:", err);

        if (auth.currentUser?.emailVerified) {
          setIsLoggedIn(true);
          setStatus("success");
          return;
        }

        setStatus("error");
        switch (err.code) {
          case "auth/expired-action-code":
            setErrorMessage(
              "This verification link has expired. Please log in to request a new verification email."
            );
            break;
          case "auth/invalid-action-code":
            setErrorMessage(
              "This link is invalid or has already been used. If you have already verified your email, you can sign in directly."
            );
            break;
          case "auth/user-disabled":
            setErrorMessage(
              "This account has been disabled. Please contact Practido support."
            );
            break;
          case "auth/user-not-found":
            setErrorMessage("No user found associated with this verification code.");
            break;
          default:
            setErrorMessage(
              err.message ||
                "Failed to verify email. Please try again or request a new verification link."
            );
        }
      }
    };

    performAction();
  }, [oobCode, mode, apiKey, navigate, dispatch]);

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!newPassword) {
      setFormError("Please enter a new password.");
      return;
    }
    if (newPassword.length < 6) {
      setFormError("Password must be at least 6 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      const actionAuth = getActionAuth(apiKey);
      await confirmPasswordReset(actionAuth, oobCode, newPassword);
      setStatus("reset_success");
      setCountdown(5);
      toast.success("Password reset successfully! You can now sign in.");
    } catch (err) {
      console.error("Password reset confirmation error:", err);
      switch (err.code) {
        case "auth/expired-action-code":
          setFormError("This password reset link has expired. Please request a new one.");
          break;
        case "auth/invalid-action-code":
          setFormError("This password reset link is invalid or has already been used.");
          break;
        case "auth/weak-password":
          setFormError("Password is too weak. Please use at least 6 characters.");
          break;
        default:
          setFormError(err.message || "Failed to update password. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDestination = () => {
    if (continueUrl) {
      try {
        const parsed = new URL(continueUrl, window.location.origin);
        if (!parsed.pathname.includes("verify-email")) {
          return parsed.pathname + parsed.search;
        }
      } catch (_) {}
    }
    return "/dashboard";
  };

  // Automatic redirect countdown on verification or reset success
  useEffect(() => {
    if (status !== "success" && status !== "reset_success") return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (status === "reset_success") {
            const loginQuery = verifiedEmail
              ? `?email=${encodeURIComponent(verifiedEmail)}&reset=true`
              : "?reset=true";
            navigate(`/login/email${loginQuery}`, { replace: true });
          } else if (isLoggedIn) {
            navigate(getDestination(), { replace: true });
          } else {
            const loginQuery = verifiedEmail
              ? `?email=${encodeURIComponent(verifiedEmail)}&verified=true`
              : "?verified=true";
            navigate(`/login/email${loginQuery}`, { replace: true });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, isLoggedIn, navigate, verifiedEmail]);

  const handleManualRedirect = () => {
    if (status === "reset_success") {
      const loginQuery = verifiedEmail
        ? `?email=${encodeURIComponent(verifiedEmail)}&reset=true`
        : "?reset=true";
      navigate(`/login/email${loginQuery}`, { replace: true });
    } else if (isLoggedIn) {
      navigate(getDestination(), { replace: true });
    } else {
      const loginQuery = verifiedEmail
        ? `?email=${encodeURIComponent(verifiedEmail)}&verified=true`
        : "?verified=true";
      navigate(`/login/email${loginQuery}`, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-gray-100 text-gray-800">
      <Header placement="register" />

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-[0_10px_35px_rgba(0,0,0,0.06)] border border-gray-100 p-8 sm:p-10 text-center transition-all duration-300">
          {/* 1. Loading / Verifying State */}
          {status === "verifying" && (
            <div className="flex flex-col items-center py-6">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-6 shadow-inner animate-pulse">
                <LuLoader2 className="w-8 h-8 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {mode === "resetPassword" ? "Verifying Reset Link" : "Verifying Your Email"}
              </h2>
              <p className="text-sm text-gray-500 max-w-sm">
                {mode === "resetPassword"
                  ? "Please wait a moment while we verify your password reset link with Practido."
                  : "Please wait a moment while we verify your email address with Practido."}
              </p>
            </div>
          )}

          {/* 2. Reset Password Form State */}
          {status === "reset_form" && (
            <div className="py-2 text-left">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 mb-3 shadow-inner">
                  <LuKeyRound className="w-7 h-7" />
                </div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Reset Password</h2>
                <p className="text-xs text-gray-500 max-w-xs">
                  Create a new secure password for your account.
                </p>
                {verifiedEmail && (
                  <span className="mt-2 text-xs font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-full px-3 py-1">
                    {verifiedEmail}
                  </span>
                )}
              </div>

              <form onSubmit={handleResetPasswordSubmit} className="flex flex-col gap-4" noValidate>
                {/* New Password */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    New Password <span className="text-[#E5383B]">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <LuLock className="absolute left-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (formError) setFormError("");
                      }}
                      placeholder="At least 6 characters"
                      required
                      className="w-full h-11 pl-10 pr-10 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all text-gray-800 placeholder-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none p-1 cursor-pointer"
                    >
                      {showPassword ? <LuEyeOff className="w-4 h-4" /> : <LuEye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">
                    Confirm New Password <span className="text-[#E5383B]">*</span>
                  </label>
                  <div className="relative flex items-center">
                    <LuLock className="absolute left-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (formError) setFormError("");
                      }}
                      placeholder="Re-enter your new password"
                      required
                      className="w-full h-11 pl-10 pr-10 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition-all text-gray-800 placeholder-gray-400"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 text-gray-400 hover:text-gray-600 focus:outline-none p-1 cursor-pointer"
                    >
                      {showConfirmPassword ? (
                        <LuEyeOff className="w-4 h-4" />
                      ) : (
                        <LuEye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {formError && (
                  <div className="text-[#E5383B] text-xs font-semibold bg-red-50 border border-red-100 rounded-lg p-2.5 text-center">
                    {formError}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 cursor-pointer disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <LuLoader2 className="w-4 h-4 animate-spin" />
                      <span>Updating Password...</span>
                    </>
                  ) : (
                    <>
                      <span>Update Password</span>
                      <LuArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                {/* Back to Sign In Link */}
                <div className="text-center mt-2">
                  <Link
                    to="/login/email"
                    className="text-xs font-bold text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    ← Back to Sign In
                  </Link>
                </div>
              </form>
            </div>
          )}

          {/* 3. Reset Password Success State */}
          {status === "reset_success" && (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6 shadow-inner">
                <LuCheckCircle2 className="w-10 h-10" />
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Password Reset!</h2>

              {verifiedEmail && (
                <p className="text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-4 py-1.5 mb-3 inline-block">
                  {verifiedEmail}
                </p>
              )}

              <p className="text-sm text-gray-600 mb-6">
                Your password has been reset successfully! You can now log in to your account with your new password.
              </p>

              <button
                type="button"
                onClick={handleManualRedirect}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 cursor-pointer"
              >
                <span>Continue to Login</span>
                <span className="text-xs opacity-75">({countdown}s)</span>
                <LuArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 4. Email Verified Success State */}
          {status === "success" && (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-6 shadow-inner">
                <LuCheckCircle2 className="w-10 h-10" />
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Email Verified!</h2>

              {verifiedEmail && (
                <p className="text-sm font-semibold text-gray-700 bg-gray-50 border border-gray-100 rounded-xl px-4 py-1.5 mb-3 inline-block">
                  {verifiedEmail}
                </p>
              )}

              <p className="text-sm text-gray-600 mb-6">
                {isLoggedIn
                  ? "Your email address has been verified successfully. Redirecting you to your dashboard..."
                  : "Your email address has been verified successfully! You can now log in to your account."}
              </p>

              <button
                type="button"
                onClick={handleManualRedirect}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 cursor-pointer"
              >
                <span>{isLoggedIn ? "Go to Dashboard" : "Continue to Login"}</span>
                <span className="text-xs opacity-75">({countdown}s)</span>
                <LuArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 5. Error State */}
          {status === "error" && (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-6 shadow-inner">
                <LuAlertTriangle className="w-9 h-9" />
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 mb-2">
                {mode === "resetPassword" ? "Password Reset Issue" : "Verification Link Issue"}
              </h2>

              <p className="text-sm text-gray-600 mb-6 leading-relaxed">{errorMessage}</p>

              <div className="w-full flex flex-col gap-3">
                {mode === "resetPassword" ? (
                  <>
                    <Link
                      to="/login/forgotPassword"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 no-underline cursor-pointer"
                    >
                      <span>Request New Reset Link</span>
                      <LuArrowRight className="w-4 h-4" />
                    </Link>

                    <Link
                      to="/login/email"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors focus:outline-none no-underline text-sm cursor-pointer"
                    >
                      <LuLogIn className="w-4 h-4" />
                      <span>Back to Sign In</span>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login/email"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-100 no-underline cursor-pointer"
                    >
                      <LuLogIn className="w-4 h-4" />
                      <span>Go to Login</span>
                    </Link>

                    <Link
                      to="/"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors focus:outline-none no-underline text-sm cursor-pointer"
                    >
                      <LuHome className="w-4 h-4" />
                      <span>Back to Home</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VerifyEmail;
